import { Timestamp } from '@marmon/shared/types/common';
import { LanguageCode } from '@marmon/shared/types/i18n';
import { User as FirebaseUser } from 'firebase/auth';
import { User, UserStatus } from '../interfaces';
import {
	createUserNotification,
	DefaultUserNotifications,
} from './notifications.utils';

export const ValidEmailRegex =
	/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export function createUser(
	payload: FirebaseUser,
	options: {
		createdAt: Timestamp;
		acceptedAt?: Timestamp;
	},
): User {
	return {
		id: payload.uid,
		displayName: payload.displayName ?? '',
		email: payload.email as string,
		photoURL: payload.photoURL,
		lang: LanguageCode.AUTO,
		// @todo rethink on how to default this when more translations are added
		langLastUsed: LanguageCode.EN,
		createdAt: options.createdAt,
		updatedAt: options.createdAt,
		deletedAt: null,
		acceptedAt: options.acceptedAt ?? null,
		requestDeletedAt: null,
		deletionStartedAt: null,
		// default to user, anything else can be changed manually in the database
		role: 'user',
		domain: determineDomain(payload.email as string),
		notifications: createUserNotification(),
	};
}

/**
 * Email host including TLD. `ada@vizashift.com` → `vizashift.com`.
 */
export function determineDomain(email: string): string {
	if (!email) return '';
	const at = email.lastIndexOf('@');
	if (at < 0) return '';
	return email
		.slice(at + 1)
		.trim()
		.toLowerCase()
		.replace(/\.$/, '');
}

/**
 * Determines what a users current status in the system is
 *
 * @param user the user to determine the status of
 * @returns the users current status in the organization
 */
export function getUserStatus(user: User): UserStatus {
	if (user.deletedAt || user.deletionStartedAt) return 'deleted';
	else if (user.acceptedAt) return 'enabled';
	else return 'pending';
}

export const UserStatusMap: {
	[key in UserStatus]: {
		value: string;
		appearance: string;
		text: string;
		icon: string;
	};
} = {
	pending: {
		value: 'pending',
		icon: '@tui.timer',
		appearance: 'warning',
		text: 'user.status.pending',
	},
	deleted: {
		value: 'deleted',
		icon: '@tui.circle-x',
		appearance: 'negative',
		text: 'user.status.deleted',
	},
	enabled: {
		value: 'enabled',
		icon: '@tui.check',
		appearance: 'positive',
		text: 'user.status.enabled',
	},
};

export function getPasswordResetUrl(token: string, email: string) {
	return `auth/reset-password?token=${token}&email=${encodeURIComponent(email)}`;
}

export const isAdmin = (user: User) => user.role === 'admin';

export function canSendEmail(user: Pick<User, 'notifications'>) {
	const notifications = user.notifications ?? DefaultUserNotifications();
	return notifications.email === true;
}
